import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaDragon, FaScissors, FaCalendarCheck, FaMapLocationDot, FaStar, FaPhone, FaEnvelope, FaInstagram, FaFacebook, FaClock, FaChevronDown, FaChevronUp, FaGoogle, FaQuoteLeft, FaXmark, FaChevronLeft, FaChevronRight } from "react-icons/fa6";

// Constants
const BOOKING_URL = "https://dragonsbarbershop.setmore.com/";
const LOGO_URL = "https://avatar.setmore.com/files/img/fovdzFZhSBo4/2e15c99f-fac5-418f-859b-6cf7b5a71ea5.jpeg?crop=1290%3B1290%3B0%3B499&w=288&h=288";
const INSTAGRAM_URL = "https://www.instagram.com/dragons.barbers/";
const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=100063479561234";
const MAPS_URL = "https://maps.app.goo.gl/3Ziu7Mb4Fb8nUKDM7";
const GOOGLE_REVIEWS_URL = "https://maps.app.goo.gl/3Ziu7Mb4Fb8nUKDM7";
const SETMORE_REVIEWS_URL = "https://dragonsbarbershop.setmore.com/reviews";
const MAPS_EMBED_SRC = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3133.652571086694!2d21.72640727616078!3d38.24116858515708!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135e49d8f1f1218b%3A0x6bf0219e935ff04c!2zRHJhZ29ucyBCYXJiZXJzaG9wIHwgzqDOrM-Ez4HOsQ!5e0!3m2!1sen!2sgr!4v1773424616237!5m2!1sen!2sgr";

const CONTACT_EMAIL = "info@dragonsbarbershop.gr";

// Local images (deployed on server)
const BARBER_IMAGES = {
  nektarios: "/photos/nektarios.webp",
  giorgos: "/photos/giorgos.webp"
};

const GALLERY_IMAGES = [
  "/photos/1.webp",
  "/photos/2.webp",
  "/photos/3.webp",
  "/photos/4.webp",
  "/photos/5.webp"
];

const HERO_BG = "https://images.unsplash.com/photo-1503951914875-452162b0f3f1?q=80&w=2000&auto=format&fit=crop";

// Services data - Greek descriptions (slug must match SEO JSON-LD in public/index.html)
const services = [
  { slug: "antriko-kourema", name: "ΑΝΤΡΙΚΟ ΚΟΥΡΕΜΑ", price: "13€", desc: "Ανδρικό κούρεμα με μοντέρνες ή κλασικές τεχνικές: fade, textured crop, crew cut, quiff και pompadour.", icon: FaScissors },
  { slug: "kourema-geneiada", name: "ΚΟΥΡΕΜΑ ΚΑΙ ΓΕΝΕΙΑΔΑ", price: "15€", desc: "Ολοκληρωμένο πακέτο men’s grooming με κούρεμα και beard trim.", icon: FaScissors },
  { slug: "peripoihsh-geneiadas", name: "ΠΕΡΙΠΟΙΗΣΗ ΓΕΝΕΙΑΔΑΣ", price: "5€", desc: "Beard trim, beard shaping και καθαρές γραμμές γενειάδας.", icon: FaScissors },
  { slug: "xyrisma", name: "ΞΥΡΙΣΜΑ", price: "8€", desc: "Παραδοσιακό ξύρισμα με φαλτσέτα και προσεγμένο φινίρισμα.", icon: FaScissors },
  { slug: "paidiko-kourema", name: "ΠΑΙΔΙΚΟ ΚΟΥΡΕΜΑ", price: "10€", desc: "Για μικρούς κυρίους έως 5 ετών.", icon: FaScissors },
  { slug: "lousimo", name: "ΛΟΥΣΙΜΟ", price: "2€", desc: "Αναζωογονητικό πλύσιμο και μασάζ.", icon: FaScissors }
];

// Barbers data
const barbers = [
  { name: "NEKTARIOS", role: "Master Barber και Ιδρυτής", image: BARBER_IMAGES.nektarios, desc: "Με πάθος για την τέχνη του barbering από το 2006." },
  { name: "GIORGOS", role: "Senior Barber", image: BARBER_IMAGES.giorgos, desc: "Ειδικός σε modern fades και styling." }
];

// Google Maps Reviews - Real reviews from the business
const googleReviews = [
  { 
    name: "Στέλιος Πολύχρονης", 
    text: "Υπέροχη ομάδα σε ένα πολύ ζεστό και φιλόξενο περιβάλλον. Ο Νεκτάριος μας περιποιείται εδώ και 8 ολόκληρα χρόνια! Συστήνουμε ανεπιφύλακτα τους dragons.", 
    rating: 5, 
    date: "Πριν από 3 μήνες",
    avatar: "Σ"
  },
  { 
    name: "Τζέλης Απόστολος", 
    text: "Φοβερή δουλειά, άψογο αποτέλεσμα, υπέροχο περιβάλλον.", 
    rating: 5, 
    date: "Πριν από 3 μήνες",
    avatar: "Τ"
  },
  { 
    name: "Κώστας Μαυρομάτης", 
    text: "Ένα από τα καλύτερα barbershop που έχω επισκεφτεί. Καταπληκτική ατμόσφαιρα και επαγγελματική εξυπηρέτηση.", 
    rating: 5, 
    date: "Πριν από 2 μήνες",
    avatar: "Κ"
  },
  { 
    name: "Γιώργος Παπαδόπουλος", 
    text: "Εξαιρετική δουλειά! Πάντα φεύγω ευχαριστημένος. Το καλύτερο barbershop της Πάτρας.", 
    rating: 5, 
    date: "Πριν από 1 μήνα",
    avatar: "Γ"
  },
  { 
    name: "Νίκος Αντωνίου", 
    text: "Επαγγελματίες με πολύ καλή διάθεση. Σας το συστήνω ανεπιφύλακτα!", 
    rating: 5, 
    date: "Πριν από 2 εβδομάδες",
    avatar: "Ν"
  },
  { 
    name: "Δημήτρης Καραγιάννης", 
    text: "Άψογη εξυπηρέτηση, τέλειο αποτέλεσμα. Θα επιστρέψω σίγουρα!", 
    rating: 5, 
    date: "Πριν από 1 εβδομάδα",
    avatar: "Δ"
  }
];

// Working hours - starts Monday, Κυριακή last
const hours = [
  { day: "Δευτέρα", hours: "Κλειστά", closed: true },
  { day: "Τρίτη", hours: "9:00 - 14:00 & 17:30 - 21:00", closed: false },
  { day: "Τετάρτη", hours: "9:00 - 15:00", closed: false },
  { day: "Πέμπτη", hours: "9:00 - 14:00 & 17:30 - 21:00", closed: false },
  { day: "Παρασκευή", hours: "9:00 - 14:00 & 17:30 - 21:00", closed: false },
  { day: "Σάββατο", hours: "9:00 - 17:00", closed: false },
  { day: "Κυριακή", hours: "Κλειστά", closed: true }
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.12 }
  }
};

// Header Component
const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl border-b border-stone-200/90 shadow-sm"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-3 group" data-testid="header-logo">
            <div className="relative">
              <img src={LOGO_URL} alt="Dragons Barbershop" className={`w-12 h-12 rounded-full object-cover ring-2 transition-all ${scrolled ? "ring-stone-300 group-hover:ring-stone-400" : "ring-white/30 group-hover:ring-white/60"}`} />
            </div>
            <span className={`font-oswald font-bold text-2xl tracking-wider hidden sm:block ${scrolled ? "text-stone-900" : "text-white"}`}>DRAGONS</span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-10">
            {[
              { href: "#services", label: "ΥΠΗΡΕΣΙΕΣ" },
              { href: "#team", label: "ΟΜΑΔΑ" },
              { href: "#gallery", label: "GALLERY" },
              { href: "#reviews", label: "ΚΡΙΤΙΚΕΣ" },
              { href: "#contact", label: "ΕΠΙΚΟΙΝΩΝΙΑ" }
            ].map((item) => (
              <a 
                key={item.href}
                href={item.href} 
                className={`font-manrope text-sm transition-all relative group ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-200 hover:text-white"}`}
                data-testid={`nav-${item.href.slice(1)}`}
              >
                {item.label}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 group-hover:w-full transition-all duration-300 ${scrolled ? "bg-dragons-red" : "bg-white"}`}></span>
              </a>
            ))}
          </nav>

          {/* ΚΑΛΕΣΤΕ ΜΑΣ + ΚΡΑΤΗΣΗ */}
          <div className="flex items-center gap-3">
            <a
              href="tel:+302611120362"
              className={`hidden sm:inline-flex px-6 py-3 font-oswald font-bold text-sm tracking-widest uppercase transition-all duration-300 border ${
                scrolled
                  ? "border-stone-400 text-stone-900 hover:bg-dragons-red hover:border-dragons-red hover:text-white"
                  : "border-white/30 text-white hover:bg-white hover:text-black"
              }`}
              data-testid="header-call-btn"
            >
              ΚΑΛΕΣΤΕ ΜΑΣ
            </a>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="relative overflow-hidden bg-white text-black px-7 py-3 font-oswald font-bold text-sm tracking-widest uppercase transition-all duration-300 hover:bg-gray-200 hidden sm:block"
              data-testid="header-book-btn"
            >
              ΚΡΑΤΗΣΗ
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden p-2 ${scrolled ? "text-stone-900" : "text-white"}`}
            data-testid="mobile-menu-btn"
          >
            <div className="w-6 h-5 flex flex-col justify-between">
              <span className={`w-full h-0.5 transition-all duration-300 ${scrolled ? "bg-stone-900" : "bg-white"} ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`w-full h-0.5 transition-all duration-300 ${scrolled ? "bg-stone-900" : "bg-white"} ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`w-full h-0.5 transition-all duration-300 ${scrolled ? "bg-stone-900" : "bg-white"} ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </div>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className={`md:hidden backdrop-blur-xl border-t py-6 ${
              scrolled ? "bg-white/98 border-stone-200" : "bg-black/95 border-white/10"
            }`}
          >
            <nav className="flex flex-col gap-4">
              <a href="#services" onClick={() => setMobileMenuOpen(false)} className={`font-manrope px-4 py-2 ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-400 hover:text-white"}`}>ΥΠΗΡΕΣΙΕΣ</a>
              <a href="#team" onClick={() => setMobileMenuOpen(false)} className={`font-manrope px-4 py-2 ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-400 hover:text-white"}`}>ΟΜΑΔΑ</a>
              <a href="#gallery" onClick={() => setMobileMenuOpen(false)} className={`font-manrope px-4 py-2 ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-400 hover:text-white"}`}>GALLERY</a>
              <a href="#reviews" onClick={() => setMobileMenuOpen(false)} className={`font-manrope px-4 py-2 ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-400 hover:text-white"}`}>ΚΡΙΤΙΚΕΣ</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)} className={`font-manrope px-4 py-2 ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-400 hover:text-white"}`}>ΕΠΙΚΟΙΝΩΝΙΑ</a>
              <a href="tel:+302611120362" onClick={() => setMobileMenuOpen(false)} className={`font-manrope px-4 py-2 ${scrolled ? "text-stone-600 hover:text-stone-900" : "text-gray-400 hover:text-white"}`}>ΚΑΛΕΣΤΕ ΜΑΣ</a>
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-black px-6 py-3 mx-4 font-oswald font-bold text-sm tracking-widest uppercase text-center"
                data-testid="mobile-book-btn"
              >
                ΚΡΑΤΗΣΗ
              </a>
            </nav>
          </motion.div>
        )}
      </div>
    </motion.header>
  );
};

// Hero Section - Enterprise style
const HeroSection = () => {
  const [totalReviews, setTotalReviews] = useState(56 + 265); // default Setmore + Google

  useEffect(() => {
    const url = process.env.REACT_APP_REVIEWS_JSON_URL || "/reviews.json";
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        const g = (data.google && typeof data.google.count === "number") ? data.google.count : 265;
        const s = (data.setmore && typeof data.setmore.count === "number") ? data.setmore.count : 56;
        setTotalReviews(g + s);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden border-b border-white/10" data-testid="hero-section">
      {/* Background Image - more visible, lighter overlay */}
      <div className="absolute inset-0">
        <img 
          src={HERO_BG}
          alt="Dragons Barbershop"
          className="w-full h-full object-cover object-center"
        />
        {/* Neutral dark gradients only on hero photo — no brand tint */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/25 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/28"></div>
      </div>

      {/* Animated Dragon Background */}
      <div className="absolute inset-0 flex items-center justify-end pr-20 pointer-events-none opacity-[0.03]">
        <FaDragon className="text-white text-[50rem] transform rotate-12" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="max-w-3xl"
        >
          {/* Badge */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-4 mb-8"
          >
            <div className="h-px w-20 bg-white/60"></div>
            <span className="font-manrope text-sm text-gray-300 tracking-[0.35em] uppercase font-medium">Από το 2012</span>
          </motion.div>

          {/* Main Headline */}
          <h1 className="font-oswald font-bold text-5xl sm:text-6xl lg:text-8xl uppercase leading-tight mb-8 tracking-tight">
            <span className="block text-white drop-shadow-lg">DRAGONS</span>
            <span className="block text-white drop-shadow-lg">BARBERSHOP</span>
          </h1>

          {/* Subheadline */}
          <p className="font-manrope text-xl md:text-2xl text-gray-300 mb-12 max-w-xl leading-relaxed">
            Premium barber υπηρεσίες στην καρδιά της Πάτρας για ανδρικό κούρεμα, fade haircut, beard trim και traditional shave.
            <span className="text-white font-semibold"> Εμπειρία, στυλ και ακρίβεια</span> σε κάθε κούρεμα.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-5">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden bg-white text-black px-10 py-5 font-oswald font-bold tracking-widest uppercase inline-flex items-center justify-center gap-3 transition-all duration-300 hover:bg-gray-200"
              data-testid="hero-book-btn"
            >
              <FaCalendarCheck className="text-lg" />
              <span>ΚΛΕΙΣΕ ΡΑΝΤΕΒΟΥ</span>
            </a>
            <a
              href="#services"
              className="group bg-transparent border-2 border-white/30 text-white px-10 py-5 font-oswald font-bold tracking-widest uppercase hover:border-white hover:bg-white/10 transition-all duration-300 inline-flex items-center justify-center gap-3 backdrop-blur-sm"
              data-testid="hero-services-btn"
            >
              ΥΠΗΡΕΣΙΕΣ
              <span className="group-hover:translate-x-1 transition-transform">→</span>
            </a>
          </div>

          {/* Rating Badge - combined Google & Setmore */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-16 inline-flex flex-wrap items-center justify-center gap-4 bg-white/5 backdrop-blur-xl border border-white/10 px-8 py-4 rounded-full"
          >
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-white text-xl" />
              ))}
            </div>
            <span className="font-teko text-3xl text-white">5.0</span>
            <div className="w-px h-8 bg-white/20" />
            <div className="flex items-center gap-3">
              <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-80 transition-opacity" title="Google" aria-label="Google">
                <FaGoogle className="text-xl" />
              </a>
              <a href={SETMORE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="text-white hover:opacity-80 transition-opacity flex items-center gap-1.5" title="Setmore" aria-label="Setmore">
                <FaCalendarCheck className="text-lg" />
                <span className="font-manrope text-xs font-medium uppercase tracking-wider">Setmore</span>
              </a>
            </div>
            <span className="font-manrope text-sm text-gray-400">{totalReviews}+ κριτικές</span>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="flex flex-col items-center gap-3 text-gray-500"
        >
          <span className="font-manrope text-xs tracking-[0.3em] uppercase">SCROLL</span>
          <FaChevronDown className="text-white text-lg" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Services Section - Black & White
const ServicesSection = () => {
  return (
    <section id="services" className="scroll-mt-28 py-28 md:py-36 bg-dragons-brand relative overflow-hidden" data-testid="services-section">
      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 mb-20">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8"
        >
          <div>
            <div className="flex items-center gap-4 mb-6">
              <FaScissors className="text-dragons-red text-2xl" />
              <span className="font-manrope text-sm text-stone-600 tracking-[0.3em] uppercase">Τι προσφέρουμε</span>
            </div>
            <h2 className="font-oswald font-bold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-stone-900">
              ΥΠΗΡΕΣΙΕΣ
            </h2>
          </div>
          <p className="font-manrope text-stone-600 max-w-md text-lg leading-relaxed">
            Κλασικές τεχνικές με σύγχρονο στυλ για low/mid/high fade, skin fade, beard trim και beard shaping.
            Κάθε υπηρεσία προσφέρεται με <span className="text-stone-900 font-semibold">προσοχή στη λεπτομέρεια</span>.
          </p>
        </motion.div>
      </div>

      {/* Services Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.slug}
              id={`service-${service.slug}`}
              variants={fadeInUp}
              className="group scroll-mt-28 relative bg-white border border-stone-200/90 shadow-sm p-8 hover:border-stone-400/60 transition-all duration-500 overflow-hidden"
              data-testid={`service-card-${index}`}
            >
              {/* Top line accent */}
              <div className="absolute top-0 left-0 w-0 h-0.5 bg-dragons-red group-hover:w-full transition-all duration-500"></div>
              
              <div className="relative z-10">
                <div className="flex justify-between items-start mb-8">
                  <div className="w-14 h-14 bg-dragons-brand rounded-lg flex items-center justify-center group-hover:bg-dragons-deep transition-colors">
                    <service.icon className="text-stone-800 text-2xl group-hover:text-white transition-colors" />
                  </div>
                  <span className="font-teko text-5xl text-stone-900">{service.price}</span>
                </div>
                <h3 className="font-oswald font-bold text-xl uppercase text-stone-900 mb-4">{service.name}</h3>
                <p className="font-manrope text-stone-600 text-sm mb-8 leading-relaxed">{service.desc}</p>
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-manrope text-sm text-stone-700 hover:text-stone-950 transition-colors inline-flex items-center gap-2 group/link"
                  data-testid={`service-book-${index}`}
                >
                  ΚΛΕΙΣΕ ΤΩΡΑ
                  <span className="group-hover/link:translate-x-2 transition-transform duration-300">→</span>
                </a>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Team Section - Black & White
const TeamSection = () => {
  return (
    <section id="team" className="py-28 md:py-36 bg-dragons-deep relative" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mb-20"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="h-px w-12 bg-dragons-red/40"></div>
            <span className="font-manrope text-sm text-stone-400 tracking-[0.3em] uppercase">Γνωρίστε μας</span>
            <div className="h-px w-12 bg-dragons-red/40"></div>
          </div>
          <h2 className="font-oswald font-bold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-white">
            Η ΟΜΑΔΑ ΜΑΣ
          </h2>
        </motion.div>

        {/* Barbers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {barbers.map((barber, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="group"
              data-testid={`barber-card-${index}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden mb-8 rounded-lg">
                {/* Image with effects */}
                <img 
                  src={barber.image}
                  alt={barber.name}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                />
                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent"></div>
                
                {/* Book button overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-white text-black py-4 font-oswald font-bold text-sm tracking-widest uppercase text-center block hover:bg-gray-200 transition-colors"
                    data-testid={`barber-book-${index}`}
                  >
                    ΚΛΕΙΣΕ ΡΑΝΤΕΒΟΥ
                  </a>
                </div>
              </div>
              
              <div className="text-center">
                <h3 className="font-oswald font-bold text-3xl uppercase text-white mb-2">{barber.name}</h3>
                <p className="font-manrope text-gray-400 text-sm uppercase tracking-widest mb-4">{barber.role}</p>
                <p className="font-manrope text-gray-300">{barber.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [totalReviews, setTotalReviews] = useState(56 + 265);

  useEffect(() => {
    const url = process.env.REACT_APP_REVIEWS_JSON_URL || "/reviews.json";
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        const g = (data.google && typeof data.google.count === "number") ? data.google.count : 265;
        const s = (data.setmore && typeof data.setmore.count === "number") ? data.setmore.count : 56;
        setTotalReviews(g + s);
      })
      .catch(() => {});
  }, []);

  return (
    <section id="about" className="py-28 md:py-36 bg-dragons-brand relative overflow-hidden" data-testid="about-section">
      {/* Background elements */}
      <div className="absolute bottom-0 left-0 pointer-events-none opacity-[0.04]">
        <FaDragon className="text-stone-900 text-[40rem]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-[4/5] overflow-hidden rounded-lg">
              <img 
                src={GALLERY_IMAGES[0]}
                alt="Dragons Barbershop Interior"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-700 grayscale"
              />
            </div>
            {/* Decorative Elements */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-stone-300/80 rounded-lg -z-10"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-white rounded-lg shadow-sm -z-10"></div>
            
            {/* Years badge */}
            <div className="absolute -bottom-4 -left-4 bg-white px-6 py-4 rounded-lg">
              <span className="font-teko text-5xl text-black">14+</span>
              <p className="font-manrope text-xs text-gray-600 uppercase tracking-wider">Χρόνια</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative z-10"
          >
            <div className="flex items-center gap-4 mb-6">
              <FaDragon className="text-dragons-red text-2xl" />
              <span className="font-manrope text-sm text-stone-600 tracking-[0.3em] uppercase">Η ιστορία μας</span>
            </div>
            
            <h2 className="font-oswald font-bold text-5xl sm:text-6xl uppercase tracking-tight text-stone-900 mb-8">
              DRAGONS<br />BARBERSHOP
            </h2>

            <div className="space-y-5 mb-10">
              <p className="font-manrope text-xl text-stone-700 leading-relaxed">
                Το Dragons Barbershop είναι ένα <span className="text-stone-900 font-semibold">modern-traditional κουρείο</span> που ιδρύθηκε το 2012 στο κέντρο της Πάτρας.
              </p>
              <p className="font-manrope text-stone-600 leading-relaxed">
                Η ομάδα μας συνδυάζει κλασικές τεχνικές κουρέματος με σύγχρονο styling για να προσφέρει καθαρά κουρέματα, sharp fades και άψογα περιποιημένα γένεια.
              </p>
              <p className="font-manrope text-stone-600 leading-relaxed">
                Στόχος μας είναι να δημιουργήσουμε έναν χώρο χαλάρωσης όπου κάθε πελάτης φεύγει με τέλειο look και αυτοπεποίθηση.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 border-t border-stone-300/80 pt-10">
              <div className="text-center md:text-left">
                <span className="font-teko text-6xl text-stone-900">14+</span>
                <p className="font-manrope text-sm text-stone-600 uppercase tracking-wider">Χρόνια</p>
              </div>
              <div className="text-center md:text-left">
                <span className="font-teko text-6xl text-stone-900">5.0</span>
                <p className="font-manrope text-sm text-stone-600 uppercase tracking-wider">Βαθμολογία</p>
              </div>
              <div className="text-center md:text-left">
                <span className="font-teko text-6xl text-stone-900">{totalReviews}+</span>
                <p className="font-manrope text-sm text-stone-600 uppercase tracking-wider">Κριτικές</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Gallery Section with lightbox
const GallerySection = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setLightboxIndex(index);
  };

  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i <= 0 ? GALLERY_IMAGES.length - 1 : i - 1));
  };

  const goNext = (e) => {
    e.stopPropagation();
    setLightboxIndex((i) => (i >= GALLERY_IMAGES.length - 1 ? 0 : i + 1));
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (lightboxIndex == null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") setLightboxIndex((i) => (i <= 0 ? GALLERY_IMAGES.length - 1 : i - 1));
      if (e.key === "ArrowRight") setLightboxIndex((i) => (i >= GALLERY_IMAGES.length - 1 ? 0 : i + 1));
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightboxIndex]);

  return (
    <section id="gallery" className="py-28 md:py-36 bg-dragons-deep relative" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16"
        >
          <div>
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dragons-red hover:text-red-400 transition-colors p-1"
                  aria-label="Instagram Dragons Barbershop"
                  data-testid="gallery-instagram-icon"
                >
                  <FaInstagram className="text-2xl" />
                </a>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-dragons-red hover:text-red-400 transition-colors p-1"
                  aria-label="Facebook Dragons Barbershop"
                  data-testid="gallery-facebook-icon"
                >
                  <FaFacebook className="text-2xl" />
                </a>
              </div>
              <span className="font-manrope text-sm text-stone-400 tracking-[0.3em] uppercase">Ακολουθήστε μας</span>
            </div>
            <h2 className="font-oswald font-bold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-white">
              GALLERY
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group font-manrope text-white hover:text-gray-300 transition-colors inline-flex items-center gap-3 text-lg"
              data-testid="gallery-instagram-link"
            >
              @dragons.barbers
              <span className="group-hover:translate-x-2 transition-transform">→</span>
            </a>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group font-manrope text-white hover:text-gray-300 transition-colors inline-flex items-center gap-3 text-lg"
              data-testid="gallery-facebook-link"
            >
              Dragons Barbershop
              <span className="group-hover:translate-x-2 transition-transform">→</span>
            </a>
          </div>
        </motion.div>

        {/* Gallery Grid */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {GALLERY_IMAGES.map((image, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              role="button"
              tabIndex={0}
              onClick={(e) => openLightbox(e, index)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openLightbox(e, index); }}
              className={`group relative overflow-hidden rounded-lg cursor-pointer ${index === 0 ? "md:col-span-2 md:row-span-2" : ""}`}
              data-testid={`gallery-image-${index}`}
            >
              <div className={`relative overflow-hidden ${index === 0 ? "aspect-square" : "aspect-square"}`}>
                <img 
                  src={image}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 grayscale group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-transparent group-hover:bg-black/30 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="font-manrope text-white text-sm tracking-widest uppercase border border-white/60 px-4 py-2">Προβολή</span>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Lightbox overlay */}
      <AnimatePresence>
      {lightboxIndex !== null && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-dragons-ink/95 backdrop-blur-sm"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Προβολή εικόνας"
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Κλείσιμο"
          >
            <FaXmark className="text-2xl" />
          </button>

          {GALLERY_IMAGES.length > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Προηγούμενο"
              >
                <FaChevronLeft className="text-xl" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Επόμενο"
              >
                <FaChevronRight className="text-xl" />
              </button>
            </>
          )}

          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative max-w-6xl max-h-[90vh] w-full mx-4 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={GALLERY_IMAGES[lightboxIndex]}
              alt={`Gallery ${lightboxIndex + 1}`}
              className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
            />
            <p className="absolute -bottom-8 left-1/2 -translate-x-1/2 font-manrope text-white/70 text-sm">
              {lightboxIndex + 1} / {GALLERY_IMAGES.length}
            </p>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
    </section>
  );
};

// Default counts (Setmore 56, Google 265). Overridden by /reviews.json from daily cron.
const DEFAULT_SETMORE_COUNT = 56;
const DEFAULT_GOOGLE_COUNT = 265;

function formatLastUpdate(isoString) {
  if (!isoString) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

// Greek script range (incl. extended). Prioritize reviews that contain Greek.
function isGreek(str) {
  if (!str || typeof str !== "string") return false;
  return /[\u0370-\u03FF\u1F00-\u1FFF]/.test(str);
}

function sortReviewsGreekFirst(reviews) {
  if (!Array.isArray(reviews) || reviews.length === 0) return reviews;
  return [...reviews].sort((a, b) => {
    const textA = (a.text || "") + (a.name || "");
    const textB = (b.text || "") + (b.name || "");
    const greekA = isGreek(textA);
    const greekB = isGreek(textB);
    if (greekA && !greekB) return -1;
    if (!greekA && greekB) return 1;
    return 0;
  });
}

// Reviews Section - fetches /reviews.json (live counts + Google review snippets), shows Τελευταία Ενημέρωση
const ReviewsSection = () => {
  const [setmoreCount, setSetmoreCount] = useState(DEFAULT_SETMORE_COUNT);
  const [googleCount, setGoogleCount] = useState(DEFAULT_GOOGLE_COUNT);
  const [liveReviews, setLiveReviews] = useState([]);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    const url = process.env.REACT_APP_REVIEWS_JSON_URL || "/reviews.json";
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => {
        if (data.setmore && typeof data.setmore.count === "number") setSetmoreCount(data.setmore.count);
        if (data.google) {
          if (typeof data.google.count === "number") setGoogleCount(data.google.count);
          if (Array.isArray(data.google.reviews) && data.google.reviews.length > 0) {
            setLiveReviews(sortReviewsGreekFirst(data.google.reviews));
          }
        }
        if (data.updated) setLastUpdate(data.updated);
      })
      .catch(() => {});
  }, []);

  const totalCount = setmoreCount + googleCount;
  const reviewsToShow = liveReviews.length > 0 ? liveReviews : sortReviewsGreekFirst(googleReviews).slice(0, 6);
  const lastUpdateFormatted = formatLastUpdate(lastUpdate);

  return (
    <section id="reviews" className="py-28 md:py-36 bg-dragons-brand relative overflow-hidden border-y border-stone-200/80" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header - one ΚΡΙΤΙΚΕΣ, both sources */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-4 bg-white border border-stone-200 shadow-sm px-8 py-4 rounded-full mb-8">
            <div className="flex gap-1">
              {[...Array(5)].map((_, i) => (
                <FaStar key={i} className="text-amber-500 text-xl" />
              ))}
            </div>
            <span className="font-teko text-4xl text-stone-900">5.0</span>
          </div>

          <h2 className="font-oswald font-bold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight text-stone-900 mb-4">
            ΚΡΙΤΙΚΕΣ
          </h2>
          <p className="font-manrope text-stone-600 text-lg">
            <span className="text-stone-900 font-semibold">{totalCount}+ κριτικές</span> πελατών · Setmore & Google
          </p>
          {lastUpdateFormatted && (
            <p className="font-manrope text-stone-500 text-sm mt-2">
              Τελευταία Ενημέρωση: {lastUpdateFormatted}
            </p>
          )}
        </motion.div>

        {/* Reviews Grid */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          {reviewsToShow.map((review, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="group relative bg-white border border-stone-200 shadow-sm p-8 rounded-xl hover:border-stone-400/70 transition-all duration-500"
              data-testid={`review-card-${index}`}
            >
              {/* Quote icon */}
              <FaQuoteLeft className="absolute top-6 right-6 text-stone-200 text-4xl" />
              
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(review.rating)].map((_, i) => (
                  <FaStar key={i} className="text-amber-500 text-lg" />
                ))}
              </div>
              
              {/* Review text */}
              <p className="font-manrope text-stone-700 mb-8 leading-relaxed line-clamp-4">"{review.text}"</p>
              
              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-dragons-deep rounded-full flex items-center justify-center">
                  <span className="font-oswald font-bold text-white text-lg">{review.avatar || (review.name || "?").charAt(0)}</span>
                </div>
                <div>
                  <p className="font-oswald font-bold text-stone-900">{review.name || "Anonymous"}</p>
                  <div className="flex items-center gap-2">
                    <FaGoogle className="text-stone-500 text-sm" />
                    <span className="font-manrope text-sm text-stone-500">{review.date || ""}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Κριτικές στο Setmore / Κριτικές στη Google */}
        <div className="text-center flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={SETMORE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-dragons-red border border-dragons-red text-white px-8 py-4 font-oswald font-bold tracking-widest hover:bg-dragons-red-hover hover:border-dragons-red-hover transition-all duration-300 shadow-md"
            data-testid="setmore-reviews-btn"
          >
            {setmoreCount} ΚΡΙΤΙΚΕΣ ΣΤΟ SETMORE
            <span>→</span>
          </a>
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-oswald font-bold tracking-widest hover:bg-gray-200 transition-all duration-300"
            data-testid="all-reviews-btn"
          >
            <FaGoogle className="text-lg" />
            {googleCount} ΚΡΙΤΙΚΕΣ ΣΤΗ GOOGLE
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-28 md:py-36 bg-dragons-deep relative border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-4 mb-6">
              <FaMapLocationDot className="text-dragons-red text-2xl" />
              <span className="font-manrope text-sm text-stone-400 tracking-[0.3em] uppercase">Βρείτε μας</span>
            </div>
            
            <h2 className="font-oswald font-bold text-5xl sm:text-6xl uppercase tracking-tight text-white mb-12">
              ΕΠΙΚΟΙΝΩΝΙΑ
            </h2>

            {/* Contact Details */}
            <div className="space-y-8 mb-12">
              <a 
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-5 group"
                data-testid="contact-address"
              >
                <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                  <FaMapLocationDot className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-oswald text-white text-lg mb-1">ΔΙΕΥΘΥΝΣΗ</p>
                  <p className="font-manrope text-gray-300 group-hover:text-white transition-colors">
                    Κορίνθου 334, Πάτρα, Αχαΐας 262 22
                  </p>
                </div>
              </a>

              <a 
                href="tel:+302611120362"
                className="flex items-start gap-5 group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                  <FaPhone className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-oswald text-white text-lg mb-1">ΤΗΛΕΦΩΝΟ</p>
                  <p className="font-manrope text-gray-300 group-hover:text-white transition-colors">
                    +30 261 112 0362
                  </p>
                </div>
              </a>

              <a 
                href={`mailto:${CONTACT_EMAIL}`}
                className="flex items-start gap-5 group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                  <FaEnvelope className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-oswald text-white text-lg mb-1">EMAIL</p>
                  <p className="font-manrope text-gray-300 group-hover:text-white transition-colors">
                    {CONTACT_EMAIL}
                  </p>
                </div>
              </a>

              <a 
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-5 group"
                data-testid="contact-instagram"
              >
                <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                  <FaInstagram className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-oswald text-white text-lg mb-1">INSTAGRAM</p>
                  <p className="font-manrope text-gray-300 group-hover:text-white transition-colors">
                    @dragons.barbers
                  </p>
                </div>
              </a>

              <a 
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-5 group"
                data-testid="contact-facebook"
              >
                <div className="w-14 h-14 bg-white/10 border border-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/20 transition-colors flex-shrink-0">
                  <FaFacebook className="text-white text-xl" />
                </div>
                <div>
                  <p className="font-oswald text-white text-lg mb-1">FACEBOOK</p>
                  <p className="font-manrope text-gray-300 group-hover:text-white transition-colors">
                    Dragons Barbershop
                  </p>
                </div>
              </a>
            </div>

            {/* Working Hours */}
            <div className="bg-white border border-stone-200 rounded-xl p-8 shadow-sm">
              <div className="flex items-center gap-4 mb-6">
                <FaClock className="text-stone-800 text-xl" />
                <span className="font-oswald font-bold text-xl uppercase text-stone-900">ΩΡΕΣ ΛΕΙΤΟΥΡΓΙΑΣ</span>
              </div>
              <div className="space-y-3">
                {hours.map((item, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-stone-200/80 last:border-0" data-testid={`hours-${index}`}>
                    <span className={`font-manrope ${item.closed ? "text-stone-500" : "text-stone-700"}`}>
                      {item.day}
                    </span>
                    <span className={`font-manrope font-medium ${item.closed ? "text-stone-500" : "text-stone-900"}`}>
                      {item.hours}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="h-full min-h-[500px] lg:min-h-full rounded-xl overflow-hidden border border-white/15 shadow-xl">
              <iframe
                title="Dragons Barbershop Location"
                src={MAPS_EMBED_SRC}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: "500px" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map"
              ></iframe>
            </div>

            {/* CTA Overlay */}
            <div className="absolute bottom-6 left-6 right-6 flex gap-4">
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-dragons-ink/90 backdrop-blur-sm border border-white/20 text-white py-4 font-oswald font-bold text-sm tracking-widest uppercase text-center hover:bg-white hover:text-black transition-colors rounded-lg"
                data-testid="contact-directions-btn"
              >
                ΟΔΗΓΙΕΣ
              </a>
              <a
                href="tel:+302611120362"
                className="flex-1 bg-white text-black py-4 font-oswald font-bold text-sm tracking-widest uppercase text-center hover:bg-gray-200 transition-colors rounded-lg"
                data-testid="contact-call-btn"
              >
                ΚΑΛΕΣΤΕ ΜΑΣ
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component - Black & White
const Footer = () => {
  return (
    <footer className="bg-dragons-brand border-t border-stone-200/90 py-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-4 mb-8">
              <img src={LOGO_URL} alt="Dragons Barbershop" className="w-16 h-16 rounded-full object-cover ring-2 ring-stone-300 grayscale" />
              <div>
                <h3 className="font-oswald font-bold text-3xl text-stone-900">DRAGONS</h3>
                <p className="font-manrope text-sm text-stone-600 tracking-wider">BARBERSHOP</p>
              </div>
            </div>
            <p className="font-manrope text-stone-600 mb-8 max-w-sm leading-relaxed">
              Modern style. Classic precision. Premium barber υπηρεσίες στην καρδιά της Πάτρας από το 2006.
            </p>
            <div className="flex gap-4">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white border border-stone-200 rounded-lg flex items-center justify-center text-stone-600 hover:text-stone-900 hover:border-stone-400 transition-all shadow-sm"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <FaInstagram className="text-xl" />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white border border-stone-200 rounded-lg flex items-center justify-center text-stone-600 hover:text-stone-900 hover:border-stone-400 transition-all shadow-sm"
                data-testid="footer-facebook"
                aria-label="Facebook"
              >
                <FaFacebook className="text-xl" />
              </a>
              <a
                href={GOOGLE_REVIEWS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-12 h-12 bg-white border border-stone-200 rounded-lg flex items-center justify-center text-stone-600 hover:text-stone-900 hover:border-stone-400 transition-all shadow-sm"
                data-testid="footer-google"
              >
                <FaGoogle className="text-xl" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-oswald font-bold text-stone-900 uppercase mb-8 text-lg">ΣΥΝΔΕΣΜΟΙ</h4>
            <nav className="flex flex-col gap-4">
              <a href="#services" className="font-manrope text-stone-600 hover:text-stone-900 transition-colors">Υπηρεσίες</a>
              <a href="#team" className="font-manrope text-stone-600 hover:text-stone-900 transition-colors">Ομάδα</a>
              <a href="#gallery" className="font-manrope text-stone-600 hover:text-stone-900 transition-colors">Gallery</a>
              <a href="#reviews" className="font-manrope text-stone-600 hover:text-stone-900 transition-colors">Κριτικές</a>
              <a href="#contact" className="font-manrope text-stone-600 hover:text-stone-900 transition-colors">Επικοινωνία</a>
            </nav>
          </div>

          {/* CTA */}
          <div>
            <h4 className="font-oswald font-bold text-stone-900 uppercase mb-8 text-lg">ΚΡΑΤΗΣΗ</h4>
            <p className="font-manrope text-stone-600 text-sm mb-8 leading-relaxed">
              Κλείστε το επόμενο ραντεβού σας online.
            </p>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-black px-8 py-4 font-oswald font-bold text-sm tracking-widest uppercase inline-block hover:bg-gray-200 transition-all duration-300"
              data-testid="footer-book-btn"
            >
              ΚΛΕΙΣΕ ΤΩΡΑ
            </a>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-stone-200/90 pt-10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-manrope text-sm text-stone-600">
            © 2026 Dragons Barbershop. All rights reserved.
          </p>
          <p className="font-manrope text-sm text-stone-600">
            Powered by:{" "}
            <a
              href="https://novelsoft.gr"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-stone-900 transition-colors"
              title="NOVELSOFT - Web Design & SEO"
            >
              NOVELSOFT (Web Design & SEO)
            </a>
          </p>
          <p className="font-manrope text-sm text-stone-600">
            Κορίνθου 334, Πάτρα | +30 261 112 0362
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page
const DragonsLanding = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="bg-dragons-brand min-h-screen">
      <Header scrolled={scrolled} />
      <HeroSection />
      <ServicesSection />
      <TeamSection />
      <AboutSection />
      <GallerySection />
      <ReviewsSection />
      <ContactSection />
      <Footer />

      {/* Scroll to top - enterprise style */}
      <motion.button
        type="button"
        onClick={scrollToTop}
        initial={false}
        animate={{ opacity: showScrollTop ? 1 : 0, pointerEvents: showScrollTop ? "auto" : "none" }}
        className="fixed bottom-6 left-6 z-50 bg-white text-black w-14 h-14 rounded-full flex items-center justify-center shadow-xl border-2 border-dragons-ink/15 hover:scale-110 hover:shadow-2xl transition-all duration-200"
        aria-label="Πήγαινε πάνω"
        title="Πήγαινε πάνω"
      >
        <FaChevronUp className="text-xl" />
      </motion.button>

      {/* Floating Book Button (Mobile) */}
      <motion.a
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1 }}
        className="fixed bottom-6 right-6 z-50 bg-white text-black w-16 h-16 rounded-full flex items-center justify-center shadow-xl md:hidden hover:scale-110 transition-transform"
        data-testid="floating-book-btn"
      >
        <FaCalendarCheck className="text-2xl" />
      </motion.a>
    </div>
  );
};

export default DragonsLanding;
